// Quotes belong to the merchant's API. Never reconstruct an offer from catalogue
// prices: those values may predate velocity, inventory or fixed-hotlist rules.
export const unavailableQuote = (reason = 'unavailable') => ({
  cashPrice: 0, creditPrice: 0, basePrice: 0,
  cashPercentage: 0, creditPercentage: 0, isBulk: false,
  cashAvailable: false, creditAvailable: false, contactForPrice: true,
  timestamp: null, priceSource: 'buylist_api', pricingError: reason
});

export const mapApiQuote = (payload, productId, condition, printing, language) => {
  const product = payload?.products?.find(item => String(item.productId) === String(productId));
  const variant = product?.variants?.find(item =>
    item.condition === condition && item.printing === printing && item.language === language);
  if (!variant?.prices) return unavailableQuote('variant_unavailable');
  const { prices, metadata = {} } = variant;
  const cashPrice = Number(prices.cashPrice);
  const creditPrice = Number(prices.creditPrice);
  if (!Number.isFinite(cashPrice) || !Number.isFinite(creditPrice) || cashPrice < 0 || creditPrice < 0) {
    return unavailableQuote('invalid_quote');
  }
  return {
    ...metadata, ...prices,
    cashPrice, creditPrice, basePrice: Number(prices.basePrice) || 0,
    cashPercentage: metadata.cashPercentage || 0,
    creditPercentage: metadata.creditPercentage || 0,
    cashAvailable: metadata.cashAvailable !== false && cashPrice > 0,
    creditAvailable: metadata.creditAvailable !== false && creditPrice > 0,
    contactForPrice: metadata.contactForPrice === true,
    hasFixedPrice: !!(metadata.hasFixedCashPrice || metadata.hasFixedCreditPrice),
    currency: payload.userSettings?.currency,
    timestamp: Date.now(), priceSource: 'buylist_api'
  };
};

export const createPublicBuylistPricing = ({ backendUrl, username, fetchImpl = (...args) => fetch(...args), now = Date.now }) => {
  const cache = new Map();
  const pending = new Map();
  const ttl = 15000;
  return async ({ productId, condition, printing, language, inventoryOffset = 0, forceRecalculate = true }) => {
    if (productId == null || productId === '') return unavailableQuote('product_unavailable');
    const offset = Math.min(10000, Math.max(0, Number(inventoryOffset) || 0));
    const key = `${productId}:${offset}`;
    const cached = cache.get(key);
    let payload;
    try {
      if (!forceRecalculate && cached && now() - cached.at < ttl) {
        payload = cached.payload;
      } else {
        if (!pending.has(key)) {
          const url = `${backendUrl}/public-buylist-prices/${encodeURIComponent(username)}/${encodeURIComponent(productId)}?inventoryOffset=${offset}`;
          const request = (async () => {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 15000);
            try {
              const response = await fetchImpl(url, { cache: 'no-store', signal: controller.signal });
              if (!response.ok) throw new Error('Pricing request failed');
              const result = await response.json();
              if (result?.success !== true || !Array.isArray(result.products)) throw new Error('Invalid pricing response');
              cache.set(key, { payload: result, at: now() });
              return result;
            } finally { clearTimeout(timeout); }
          })().finally(() => pending.delete(key));
          pending.set(key, request);
        }
        payload = await pending.get(key);
      }
      return mapApiQuote(payload, productId, condition, printing, language);
    } catch (error) {
      return unavailableQuote('api_unavailable');
    }
  };
};
